import { useIntl, defineMessages } from 'react-intl';
import { CheckIcon, AlertOutlineIcon, CloseIcon } from '@eventbrite/marmalade';
import { useFollowContext } from '../useFollowContext';
import type { FollowNotificationType } from '../useFollowContext';
import styles from './FollowNotificationBar.module.scss';

const messages = defineMessages({
  followSuccess: {
    id: "You're now following {name}. See more in your <profileLink>profile</profileLink>",
    defaultMessage:
      "You're now following {name}. See more in your <profileLink>profile</profileLink>",
  },
  followError: {
    id: 'Unable to follow {name}. Please try again in a few minutes',
    defaultMessage: 'Unable to follow {name}. Please try again in a few minutes',
  },
  unfollowError: {
    id: 'Unable to unfollow {name}. Please try again in a few minutes',
    defaultMessage: 'Unable to unfollow {name}. Please try again in a few minutes',
  },
  closeNotification: {
    id: 'Close notification',
    defaultMessage: 'Close notification',
  },
});

function getBarClassName(type: FollowNotificationType): string {
  return type === 'success' ? styles.success : styles.error;
}

export function FollowNotificationBar() {
  const intl = useIntl();
  const { notification, dismissNotification } = useFollowContext();

  if (!notification) return null;

  const { type, organizerName } = notification;
  const isSuccess = type === 'success';

  const icon = isSuccess ? (
    <CheckIcon size="small" aria-hidden />
  ) : (
    <AlertOutlineIcon size="small" aria-hidden />
  );

  let messageDescriptor;
  if (type === 'success') {
    messageDescriptor = messages.followSuccess;
  } else if (type === 'follow-error') {
    messageDescriptor = messages.followError;
  } else {
    messageDescriptor = messages.unfollowError;
  }

  const content =
    type === 'success'
      ? intl.formatMessage(messageDescriptor, {
          name: organizerName,
          profileLink: (chunks: React.ReactNode) => <a href="/mytickets/">{chunks}</a>,
        })
      : intl.formatMessage(messageDescriptor, { name: organizerName });

  return (
    <div
      className={`${styles.bar} ${getBarClassName(type)}`}
      role="alert"
      data-testid="follow-notification-bar"
    >
      <span className={styles.icon}>{icon}</span>
      <span className={styles.message}>{content}</span>
      <button
        type="button"
        className={styles.closeButton}
        onClick={dismissNotification}
        aria-label={intl.formatMessage(messages.closeNotification)}
        data-testid="follow-notification-close"
      >
        <CloseIcon size="small" aria-hidden />
      </button>
    </div>
  );
}
